import Cookies from 'universal-cookie'
import { buildCookieScope, parentDomains, scopedCookieNameFor, UNSCOPED_LEGACY_NAMES } from './scope'

const rawCookies = new Cookies()

const day = 60 * 60 * 24 * 1000

// Server envs span two subdomains (app-* / legacy-*); REACT_APP_COOKIE_DOMAIN sets the
// shared parent domain (e.g. .dev.estacaofuturo.com.br) so the session survives the hop.
// Empty in local dev → host-only on localhost (already shared across ports).
const envCookieDomain = process.env.REACT_APP_COOKIE_DOMAIN
const localDomain = (envCookieDomain != null && envCookieDomain !== '')
  ? envCookieDomain
  : window.location.hostname

// Both sides of the app-*/legacy-* hop must derive the SAME suffix or the session is lost on
// the hop, so this reads REACT_APP_TENANT_DOMAIN, which CI writes for the shell and for the
// legacy SPAs alike — never VITE_TENANT, whose values differ from TENANT_DOMAIN's
// (zing-cdl vs CDL, zing-teia vs TEIA). Why names are scoped at all: see ./scope.
// The shell is Vite and only exposes what vite.config lists; the legacy SPA is CRA and inlines
// every REACT_APP_* on its own. A var missing from vite.config is NOT a build error — it just
// arrives undefined, the shell scopes its cookies to "unknown" while the legacy scopes them to
// the tenant, and from then on neither side can see the other's session: the app-*/legacy-* hop
// bounces forever and a login looks like it was refused. TENANT_DOMAIN carries the same value
// and is already exposed to the shell, so it is kept as a second source, and the warning below
// makes the gap visible instead of silent.
const tenantDomain = process.env.REACT_APP_TENANT_DOMAIN ?? process.env.TENANT_DOMAIN

export const cookieScope = buildCookieScope(tenantDomain, process.env.REACT_APP_ENV)

if (cookieScope.startsWith('unknown')) {
  console.error(
    `[cookies] tenant nao resolvido: os cookies serao gravados com o sufixo "${cookieScope}". ` +
    'O shell e o legado vao divergir e a sessao se perde no hop. ' +
    'Confira REACT_APP_TENANT_DOMAIN em ci/write-env.sh e no define do vite.config.'
  )
}

export const scopedCookieName = (cookieName: string): string => scopedCookieNameFor(cookieName, cookieScope)

// Every read and write goes through this wrapper, so a cookie cannot be added unscoped by
// accident. Never construct `new Cookies()` elsewhere — it bypasses the scoping silently and
// the bug comes back on that one screen only.
export const cookies = {
  get: (cookieName: string, options?) => rawCookies.get(scopedCookieName(cookieName), options),
  set: (cookieName: string, cookieData, options?): void => {
    rawCookies.set(scopedCookieName(cookieName), cookieData, options)
  },
  remove: (cookieName: string, options?): void => {
    rawCookies.remove(scopedCookieName(cookieName), options)
  }
}

// the cookies are created to have a default duration of one day
export const setCookie = (cookieName: string, cookieData, milisecDuration = day): void => {
  cookies.set(cookieName, cookieData, {
    path: '/',
    expires: new Date(Date.now() + milisecDuration),
    domain: localDomain,
    httpOnly: false,
    // A session credential served over HTTPS: Lax still covers the app-*/legacy-* hop because
    // both hosts share a registrable domain. `secure` is conditional so local http://localhost
    // keeps working — a Secure cookie is simply not stored over plain http.
    sameSite: 'lax',
    secure: window.location.protocol === 'https:'
  })
}

export const removeCookie = (cookieName: string): void => {
  cookies.remove(cookieName, { path: '/', domain: localDomain })
}

export const clearUnscopedLegacyCookies = (): void => {
  try {
    const scopes = [undefined, ...parentDomains(window.location.hostname)]
    UNSCOPED_LEGACY_NAMES.forEach((cookieName) => {
      scopes.forEach((domain) => {
        rawCookies.remove(cookieName, domain === undefined ? { path: '/' } : { path: '/', domain })
      })
    })
  } catch {
    // Never let cookie hygiene break boot.
  }
}

clearUnscopedLegacyCookies()
